import type { TeamId } from './game';
import { routeZoneCallsign, routeZoneLabel } from './teamplay';
import { clamp01 } from './stringUtils';

export type RouteZoneReadabilityInput = {
  id?: string | null;
  label?: string | null;
  callsign?: string | null;
  ownerTeam?: TeamId | null;
  captureTargetTeam?: TeamId | null;
  captureProgress?: number;
  contested?: boolean;
  carrierInside?: boolean;
  scoringMultiplier?: number;
};

export type RouteZonePressureState =
  | 'contested'
  | 'enemy-pressure'
  | 'friendly-hold'
  | 'carrier-pressure'
  | 'bonus-scoring'
  | 'neutral';

export type RouteZoneReadability = {
  lane: string;
  pressureState: RouteZonePressureState;
  status: 'contested' | 'threat' | 'hold' | 'neutral';
  statusLabel: string;
  cueText: string;
  ringColor: string;
  beamColor: string;
  beamOpacity: number;
  ringOpacity: number;
  beaconScale: number;
};


function normalizeTeam(team: TeamId | null | undefined): TeamId | null {
  return team === 'red' || team === 'blue' ? team : null;
}

function teamTone(team: TeamId | null): string {
  if (team === 'red') {
    return '#ff8f7d';
  }
  if (team === 'blue') {
    return '#6ed6ff';
  }
  return '#a9bfd2';
}

export function resolveRouteZoneReadability(input: RouteZoneReadabilityInput): RouteZoneReadability {
  const ownerTeam = normalizeTeam(input.ownerTeam);
  const captureTargetTeam = normalizeTeam(input.captureTargetTeam);
  const contested = Boolean(input.contested);
  const captureProgress = clamp01(input.captureProgress ?? 0);
  const scoringMultiplier = Math.max(1, input.scoringMultiplier ?? 1);
  const carrierInside = Boolean(input.carrierInside);
  const lane = input.callsign ?? routeZoneCallsign(input.id ?? undefined);
  const laneLabel = input.label ?? routeZoneLabel(input.id ?? undefined);
  const laneLabelUpper = laneLabel.toUpperCase();
  const pressureProgress = Math.max(0.18, captureProgress);
  const bonusEmphasis = Boolean(ownerTeam && !carrierInside && scoringMultiplier > 1.12);
  const enemyPressure =
    Boolean(captureTargetTeam && captureTargetTeam !== ownerTeam) || Boolean(!ownerTeam && captureTargetTeam);
  const pressureState: RouteZonePressureState = contested
    ? 'contested'
    : carrierInside && ownerTeam
      ? 'carrier-pressure'
      : enemyPressure
        ? 'enemy-pressure'
        : bonusEmphasis
          ? 'bonus-scoring'
          : ownerTeam
            ? 'friendly-hold'
            : 'neutral';

  if (contested) {
    return {
      lane,
      pressureState,
      status: 'contested',
      statusLabel: `${laneLabelUpper} CONTESTED`,
      cueText: `Collapse on ${laneLabel}`,
      ringColor: '#ffd166',
      beamColor: '#ffe7a8',
      beamOpacity: 0.58,
      ringOpacity: 0.42,
      beaconScale: 1.2,
    };
  }

  if (carrierInside && ownerTeam) {
    return {
      lane,
      pressureState,
      status: 'threat',
      statusLabel: `${laneLabelUpper} CARRIER HOT`,
      cueText: `Carrier pressure on ${laneLabel}`,
      ringColor: '#ffb36b',
      beamColor: '#ff8f7d',
      beamOpacity: 0.44 + pressureProgress * 0.18,
      ringOpacity: 0.3 + pressureProgress * 0.22,
      beaconScale: 1.14 + pressureProgress * 0.18,
    };
  }

  if (enemyPressure) {
    const threatTone = captureTargetTeam ? teamTone(captureTargetTeam) : '#ff8f7d';
    const pressure = Math.max(0.12, captureProgress);
    return {
      lane,
      pressureState,
      status: 'threat',
      statusLabel: `${laneLabelUpper} PRESSURE`,
      cueText: `Enemy pressure on ${laneLabel}`,
      ringColor: threatTone,
      beamColor: '#ffc28f',
      beamOpacity: 0.32 + pressure * 0.38,
      ringOpacity: 0.24 + pressure * 0.32,
      beaconScale: 1.06 + pressure * 0.2,
    };
  }

  if (bonusEmphasis) {
    const bonusBoost = Math.min(1, (scoringMultiplier - 1.12) / 0.58);
    return {
      lane,
      pressureState,
      status: 'hold',
      statusLabel: `${laneLabelUpper} BONUS`,
      cueText: `Bonus scoring on ${laneLabel}`,
      ringColor: teamTone(ownerTeam),
      beamColor: '#8ef0c9',
      beamOpacity: 0.3 + bonusBoost * 0.2,
      ringOpacity: 0.23 + bonusBoost * 0.18,
      beaconScale: 1.04 + bonusBoost * 0.16,
    };
  }

  if (ownerTeam) {
    const controlBoost = Math.min(0.18, (scoringMultiplier - 1) * 0.3);
    return {
      lane,
      pressureState,
      status: 'hold',
      statusLabel: `${laneLabelUpper} HOLD`,
      cueText: `Hold ${laneLabel}`,
      ringColor: teamTone(ownerTeam),
      beamColor: teamTone(ownerTeam),
      beamOpacity: 0.24 + controlBoost,
      ringOpacity: 0.2 + controlBoost,
      beaconScale: 1 + controlBoost * 0.6,
    };
  }

  return {
    lane,
    pressureState,
    status: 'neutral',
    statusLabel: `${laneLabelUpper} OPEN`,
    cueText: `Open ${laneLabel}`,
    ringColor: '#a9bfd2',
    beamColor: '#d4e3ef',
    beamOpacity: 0.18,
    ringOpacity: 0.16,
    beaconScale: 0.96,
  };
}
